import { useState } from 'react';

import { Avatar, Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import CircleMemberCard from '@/karehero-app/onboarding/CircleMemberCard';
import InviteSheet from '@/karehero-app/onboarding/InviteSheet';
import {
  type CircleMember,
  MEMBERS,
  PERMISSIONS,
} from '@/karehero-app/onboarding/Onboarding.content';

// One thing a member can do, as a line rather than as a control.
//
// It was a card with a switch, which asked a question we do not mean: access
// follows from the relationship, so there is nothing here to turn off. A tick
// and the sentence is the whole row.
const PermissionRow = ({ label, desc }: { label: string; desc: string }) => (
  <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={1.5}>
    <Box sx={{ pt: 0.5, color: 'primary.main' }}>
      <Icon icon={'check-circle' as never} fontSize={16} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant='h6'>{label}</Typography>
      <Typography variant='body2' color='text.secondary'>
        {desc}
      </Typography>
    </Box>
  </Stack>
);
/**
 * The care circle: who is in it, what each of them can see, and the way to
 * invite somebody else.
 *
 * Lifted out of Onboarding's last step so the intake can ask the same question
 * without a second copy of it. The two ask it in different places and frame it
 * differently — onboarding as the last thing to finish, the intake as
 * something to do while the reading happens — but the circle itself is one
 * object, and the invite sheet and the permissions sheet are its behaviour
 * rather than that step's.
 *
 * What stayed behind in Onboarding is that step's chrome: the progress bar, the
 * header, Do this later and the Next row. None of it is true of the circle, and
 * all of it is wrong in the intake.
 */
const CareCircle = ({
  initialOpenMember = null,
  initialInviteOpen = false,
}: {
  /** Jump-state for handover minis: a sheet already open. */
  initialOpenMember?: number | null;
  initialInviteOpen?: boolean;
}) => {
  // Index of the member whose permissions sheet is open, or null.
  const [openMember, setOpenMember] = useState<number | null>(
    initialOpenMember,
  );
  const [members, setMembers] = useState(MEMBERS);
  const [allPerms, setAllPerms] = useState<Record<string, boolean>[]>(() =>
    MEMBERS.map((m) => ({ ...m.perms })),
  );
  const [isInviteOpen, setIsInviteOpen] = useState(initialInviteOpen);
  const addMember = (member: CircleMember) => {
    setMembers((prev) => [...prev, member]);
    setAllPerms((prev) => [...prev, { ...member.perms }]);
    setIsInviteOpen(false);
  };

  return (
    <>
      <Stack spacing={3}>
        {members.map((member) => (
          // No "See what they can access" for now. What a member can see is
          // settled enough to state and not settled enough to show, so the
          // card says who is in the circle and stops there. The sheet it
          // opened is still below, reachable from props, because this is a
          // row being hidden rather than a thing being dropped.
          <CircleMemberCard key={member.name} member={member} />
        ))}

        <Button
          variant='secondary'
          size='lg'
          isFullWidth
          ariaLabel='Add another'
          onPress={() => setIsInviteOpen(true)}
        >
          + Add another
        </Button>
      </Stack>

      {isInviteOpen && (
        <InviteSheet
          onSend={addMember}
          onClose={() => setIsInviteOpen(false)}
        />
      )}

      {openMember !== null && (
        <BottomSheet
          variant='hug'
          showHandleClose={false}
          onClose={() => setOpenMember(null)}
        >
          <Stack sx={{ pb: 1 }} spacing={2}>
            <Box
              sx={{
                alignSelf: 'center',
                width: 36,
                height: 4,
                borderRadius: '2px',
                bgcolor: 'custom.neutral.300',
              }}
            />
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: 'custom.limeGreen.500',
                  color: 'primary.main',
                  fontSize: 14,
                }}
              >
                {members[openMember].initials}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='h4'>
                  {members[openMember].name} can&hellip;
                </Typography>
                {/* The relationship on its own. It used to say the access was
                    "suggested for this relationship", which was true while the
                    switches were there to overrule it. */}
                <Typography variant='body2' color='text.secondary'>
                  {members[openMember].role}
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={2}>
              {/* Only the lines that are true of them — a financial advisor
                  never has the 'actions' key, so it is not listed. */}
              {PERMISSIONS.filter((p) => p.key in allPerms[openMember]).map(
                (p) => (
                  <PermissionRow key={p.key} label={p.label} desc={p.desc} />
                ),
              )}
            </Stack>
            <Button
              variant='secondary'
              size='lg'
              isFullWidth
              ariaLabel='Close'
              onPress={() => setOpenMember(null)}
            >
              Close
            </Button>
          </Stack>
        </BottomSheet>
      )}
    </>
  );
};

export default CareCircle;
